import { Controller } from '@hotwired/stimulus';
import Dropzone from 'dropzone';

import DropzoneWithDirectUpload from '../../lib/dropzone_with_direct_upload';
import { t, numberToHumanSize } from '../../lib/rs/i18n';

Dropzone.autoDiscover = false;

export default class extends Controller {
  static targets = [
    'dropzone',
    'submit',
    'previewsContainer',
    'previewTemplate',
  ];

  static values = {
    url: String,
    paramName: String,
    acceptedFiles: String,
    imageMinWidth: Number,
    videoMaxSize: Number,
  };

  connect() {
    this.isUploadingFiles = false;
    this.dropzone = this.createDropzone();
  }

  disconnect() {
    this.dropzone.destroy();
  }

  uploadFiles() {
    this.submitTarget.disabled = true;
    this.isUploadingFiles = true;
    this.dropzone.processQueue();
  }

  previewsContainerTargetConnected(target) {
    this.hideElement(target);
  }

  submitTargetConnected(target) {
    this.hideElement(target);
  }

  createDropzone() {
    const dropzone = new DropzoneWithDirectUpload(this.dropzoneTarget, {
      url: this.urlValue,
      previewsContainer: this.previewsContainerTarget,
      previewTemplate: this.previewTemplateTarget.innerHTML,
      headers: { 'X-CSRF-Token': this.csrfToken },
      paramName: this.paramNameValue,
      acceptedFiles: this.acceptedFilesValue,
      autoProcessQueue: false,
      thumbnailWidth: null,
      thumbnailHeight: null,

      accept: (file, done) => {
        this.validate(file, (message) => {
          done(message);
          this.updateSubmitTarget();
        });
      },
    });

    this.bindDropzoneEvents(dropzone);

    return dropzone;
  }

  bindDropzoneEvents(dropzone) {
    dropzone.on('addedfile', (file) => {
      if (file.type.startsWith('video/')) {
        this.createVideoPreview(file);
      }

      this.updateSubmitTarget();
      this.showElement(this.previewsContainerTarget);
    });

    dropzone.on('removedfile', () => {
      this.updateSubmitTarget();
    });

    dropzone.on('reset', () => {
      this.updateSubmitTarget();
      this.hideElement(this.previewsContainerTarget);
    });

    dropzone.on('queuecomplete', () => {
      if (this.isUploadingFiles) {
        const url = new URL(window.location.href);

        url.searchParams.set('count', this.acceptedFilesCount);

        window.Turbo.visit(url.toString());
      }
    });

    dropzone.on('complete', () => {
      if (this.isUploadingFiles) {
        this.dropzone.processQueue();
      }
    });
  }

  createVideoPreview(file) {
    const { previewElement } = file;
    const thumbnail = previewElement.querySelector('[data-dz-thumbnail]');
    const video = document.createElement('video');
    const videoURL = URL.createObjectURL(file);

    video.src = videoURL;
    video.controls = true;
    video.className = thumbnail.className;

    thumbnail.replaceWith(video);

    video.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(videoURL);
    });
  }

  validate(file, done) {
    if (file.type.startsWith('image/')) {
      this.validateImage(file, done);
    } else if (file.type.startsWith('video/')) {
      this.validateVideo(file, done);
    } else {
      done();
    }
  }

  validateImage(file, done) {
    const minWidth = this.imageMinWidthValue;
    const image = new Image();
    const imageURL = URL.createObjectURL(file);

    image.onload = () => {
      if (image.width < minWidth) {
        done(
          `Images ${t(
            'errors.messages.dimension_width_greater_than_or_equal_to',
            {
              length: minWidth,
            }
          )}`
        );
      } else {
        done();
      }

      URL.revokeObjectURL(imageURL);
    };

    image.src = imageURL;
  }

  validateVideo(file, done) {
    const maxSize = this.videoMaxSizeValue;

    if (file.size > maxSize) {
      done(
        `File ${t('errors.messages.file_size_not_less_than_or_equal_to', {
          max_size: numberToHumanSize(maxSize),
          file_size: numberToHumanSize(file.size),
        })}`
      );
    } else {
      done();
    }
  }

  updateSubmitTarget() {
    if (this.acceptedFilesCount === 0) {
      this.hideElement(this.submitTarget);
    } else {
      this.showElement(this.submitTarget);
    }
  }

  get acceptedFilesCount() {
    return this.dropzone.getAcceptedFiles().length;
  }

  get csrfToken() {
    const metaElements = document.getElementsByTagName('meta');
    const csrfMetaElement = metaElements.namedItem('csrf-token');

    return csrfMetaElement.content;
  }

  hideElement(element) {
    element.classList.add('hidden!');
  }

  showElement(element) {
    element.classList.remove('hidden!');
  }
}
